// Formatting utility functions
import { getCurrencySymbol, getUserLocalization } from './i18n';

// Get user's localization preferences
const getUserDefaults = () => {
    try {
        return getUserLocalization();
    } catch {
        return { locale: 'en-US', currency: 'USD', timezone: 'UTC', dateFormat: 'MM/dd/yyyy' };
    }
};

export const formatCurrency = (amount: number, currency?: string, locale?: string): string => {
    const userDefaults = getUserDefaults();
    const currencyCode = currency || userDefaults.currency;
    const userLocale = locale || userDefaults.locale;

    try {
        return new Intl.NumberFormat(userLocale, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    } catch (error) {
        // Fallback for unsupported currencies/locales
        const symbol = getCurrencySymbol(currencyCode);
        return `${symbol}${amount.toFixed(2)}`;
    }
}

// Enhanced currency formatting with compact notation for large numbers
export const formatCurrencyCompact = (amount: number, currency?: string, locale?: string): string => {
    const userDefaults = getUserDefaults();
    const currencyCode = currency || userDefaults.currency;
    const userLocale = locale || userDefaults.locale;

    try {
        const formatter = new Intl.NumberFormat(userLocale, {
            style: 'currency',
            currency: currencyCode,
            notation: Math.abs(amount) >= 1000 ? 'compact' : 'standard',
            compactDisplay: 'short',
            minimumFractionDigits: Math.abs(amount) >= 1000 ? 0 : 2,
            maximumFractionDigits: Math.abs(amount) >= 1000 ? 1 : 2
        });

        return formatter.format(amount);
    } catch (error) {
        // Fallback for unsupported currencies/locales
        const symbol = getCurrencySymbol(currencyCode);
        if (Math.abs(amount) >= 1000) {
            const absAmount = Math.abs(amount);
            const compactAmount = absAmount >= 1000000000
                ? (amount / 1000000000).toFixed(1) + 'B'
                : absAmount >= 1000000
                ? (amount / 1000000).toFixed(1) + 'M'
                : (amount / 1000).toFixed(1) + 'K';
            return `${symbol}${compactAmount}`;
        }
        return `${symbol}${amount.toFixed(2)}`;
    }
}

// Smart currency formatter that automatically chooses between full and compact format
export const formatCurrencyAuto = (amount: number, currency?: string, locale?: string, forceCompact = false): string => {
    // Use compact format for amounts >= 10,000 or when forced
    if (forceCompact || Math.abs(amount) >= 10000) {
        return formatCurrencyCompact(amount, currency, locale);
    }

    return formatCurrency(amount, currency, locale);
}

export const formatDate = (dateString: string, locale?: string): string => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid date'

    const userDefaults = getUserDefaults();
    const userLocale = locale || userDefaults.locale;

    return new Intl.DateTimeFormat(userLocale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    }).format(date)
}

export const formatDateTime = (dateString: string, locale?: string): string => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid date'

    const userDefaults = getUserDefaults();
    const userLocale = locale || userDefaults.locale;

    return new Intl.DateTimeFormat(userLocale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date)
}

export const formatTimeAgo = (dateString: string): string => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid date'
    const now = new Date()
    const diffInMs = now.getTime() - date.getTime()

    const seconds = Math.floor(diffInMs / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)
    const weeks = Math.floor(days / 7)
    const months = Math.floor(days / 30)
    const years = Math.floor(days / 365)

    if (seconds < 60) {
        return 'Just now'
    } else if (minutes < 60) {
        return `${minutes}m ago`
    } else if (hours < 24) {
        return `${hours}h ago`
    } else if (days < 7) {
        return `${days}d ago`
    } else if (weeks < 4) {
        return `${weeks}w ago`
    } else if (months < 12) {
        return `${months}mo ago`
    } else {
        return `${years}y ago`
    }
}

export const formatPhoneNumber = (phoneNumber: string, countryCode?: string): string => {
    if (!phoneNumber) return '';

    // Import phone formatting from i18n utils
    try {
        const { formatPhoneNumber: formatPhoneIntl } = require('./i18n');
        return formatPhoneIntl(phoneNumber, countryCode || 'US');
    } catch (error) {
        // Fallback formatting
        const cleaned = phoneNumber.replace(/\D/g, '');

        // Basic international format detection
        if (cleaned.startsWith('1') && cleaned.length === 11) {
            // North American format
            return cleaned.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
        } else if (cleaned.startsWith('233') && cleaned.length === 12) {
            // Ghana format
            return cleaned.replace(/(\d{3})(\d{2})(\d{3})(\d{4})/, '+$1 $2 $3 $4');
        } else if (cleaned.startsWith('44') && cleaned.length >= 11) {
            // UK format
            return cleaned.replace(/(\d{2})(\d{2,4})(\d{4,6})/, '+$1 $2 $3');
        }

        // Generic international format
        return phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
    }
}

export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export const formatDistance = (distanceInKm: number): string => {
    if (distanceInKm < 1) {
        return `${Math.round(distanceInKm * 1000)}m away`
    } else if (distanceInKm < 10) {
        return `${distanceInKm.toFixed(1)}km away`
    } else {
        return `${Math.round(distanceInKm)}km away`
    }
}

export const formatDuration = (durationString: string): string => {
    // Clean up duration strings
    return durationString.replace(/\b(\d+)\s*(hour|hr|h)\b/gi, '$1h')
        .replace(/\b(\d+)\s*(minute|min|m)\b/gi, '$1m')
        .replace(/\b(\d+)\s*(day|d)\b/gi, '$1d')
        .replace(/\b(\d+)\s*(week|wk|w)\b/gi, '$1w')
        .replace(/\b(\d+)\s*(month|mo)\b/gi, '$1mo')
}

export const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text
    return text.slice(0, maxLength).trim() + '...'
}

export const capitalizeFirst = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export const formatCategoryName = (category: string): string => {
    return category.split('-').map(word => capitalizeFirst(word)).join(' ')
}

// Utility for creating URL-friendly slugs
export const createSlug = (text: string): string => {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '') // Remove special characters
        .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
        .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
}

// Parse query parameters from URL
export const parseQueryParams = (search: string): Record<string, string> => {
    const params = new URLSearchParams(search)
    const result: Record<string, string> = {}

    for (const [key, value] of params.entries()) {
        result[key] = value
    }

    return result
}

// Build query string from object
export const buildQueryString = (params: Record<string, any>): string => {
    const searchParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, String(value))
        }
    })

    return searchParams.toString()
}